/** MASTER-DATA-FOUNDATION-000001 — Warehouse Master client */
import { asArray, fetchJSON as fetchAPI, unwrapEnvelopeData } from './client';
import { getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_AMAZON_CONNECT_API ?? '/api/v1/amazon';

export type WarehouseType =
  | 'amazon_fc'
  | 'amazon_ixd'
  | 'seller_warehouse'
  | 'seller_flex'
  | 'third_party'
  | 'unknown';

export type WarehouseStatus = 'active' | 'inactive' | 'unknown';

export type Warehouse = {
  id: string;
  tenant_id?: string;
  warehouse_code: string;
  warehouse_name: string;
  marketplace?: string;
  country?: string;
  state?: string;
  city?: string;
  region?: string;
  latitude?: number | null;
  longitude?: number | null;
  warehouse_type: WarehouseType;
  status: WarehouseStatus;
  created_at?: string;
  updated_at?: string;
  last_verified?: string | null;
  verified_by?: string;
  remarks?: string;
};

export type ResolveResult = {
  warehouse_code: string;
  found: boolean;
  mapped: boolean;
  status: WarehouseStatus;
  warehouse?: Warehouse;
  warehouse_name?: string;
  city?: string;
  state?: string;
  region?: string;
  warehouse_type?: WarehouseType;
  validation: string;
};

export type WarehouseList = {
  items: Warehouse[];
  total: number;
  limit?: number;
  offset?: number;
};

export type WarehouseAudit = {
  id: string;
  warehouse_code: string;
  action: string;
  actor?: string;
  detail?: Record<string, unknown>;
  created_at: string;
};

function get<T>(path: string): Promise<T> {
  return fetchAPI<T>(API_BASE, path);
}

export const warehouseMasterApi = {
  list: (q = '', status = '', limit = 100, offset = 0) => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (status) p.set('status', status);
    p.set('limit', String(limit));
    p.set('offset', String(offset));
    return get<WarehouseList>(`/master-data/warehouses?${p}`);
  },
  get: (idOrCode: string) => get<Warehouse>(`/master-data/warehouses/${encodeURIComponent(idOrCode)}`),
  save: (body: Partial<Warehouse>) =>
    fetchAPI<Warehouse>(API_BASE, '/master-data/warehouses', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: string, body: Partial<Warehouse>) =>
    fetchAPI<Warehouse>(API_BASE, `/master-data/warehouses/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  remove: (id: string) =>
    fetchAPI<{ status: string }>(API_BASE, `/master-data/warehouses/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  resolve: (code: string, marketplace = '', auto = true) => {
    const p = new URLSearchParams({ code, auto: auto ? 'true' : 'false' });
    if (marketplace) p.set('marketplace', marketplace);
    return get<ResolveResult>(`/master-data/warehouses/resolve?${p}`);
  },
  observe: (codes: string[], marketplace = '') =>
    fetchAPI<{ observed: number }>(API_BASE, '/master-data/warehouses/observe', {
      method: 'POST',
      body: JSON.stringify({ warehouse_codes: codes, marketplace }),
    }),
  audit: (code = '', limit = 50) => {
    const p = new URLSearchParams({ limit: String(limit) });
    if (code) p.set('code', code);
    return get<{ items: WarehouseAudit[] }>(`/master-data/warehouses/audit?${p}`).then((r) =>
      asArray<WarehouseAudit>(r?.items),
    );
  },
  exportUrl: () => `${API_BASE}/master-data/warehouses/export`,
  async exportCsv(): Promise<Blob> {
    const res = await fetch(`${API_BASE}/master-data/warehouses/export`, {
      headers: { ...getScopeHeaders(), Accept: 'text/csv' },
    });
    if (!res.ok) throw new Error(`export failed: ${res.status}`);
    return res.blob();
  },
  async importCsv(file: File): Promise<{ created: number; updated: number; errors: number }> {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/master-data/warehouses/import`, {
      method: 'POST',
      headers: { ...getScopeHeaders() },
      body: fd,
    });
    if (!res.ok) throw new Error(`import failed: ${res.status} ${await res.text()}`);
    const body = await res.json();
    return unwrapEnvelopeData(body);
  },
};

export const WAREHOUSE_TYPES: { value: WarehouseType; label: string }[] = [
  { value: 'amazon_fc', label: 'Amazon FC (Regular)' },
  { value: 'amazon_ixd', label: 'Amazon IXD' },
  { value: 'seller_warehouse', label: 'Seller Warehouse' },
  { value: 'seller_flex', label: 'Seller Flex / SellerFlex Warehouse' },
  { value: 'third_party', label: 'Third Party' },
  { value: 'unknown', label: 'Unknown' },
];

/** Feeder sheet column order (MASTER-DATA-FOUNDATION inbound). */
export const WAREHOUSE_FEED_COLUMNS = [
  'warehouse_code',
  'warehouse_name',
  'marketplace',
  'country',
  'state',
  'city',
  'region',
  'latitude',
  'longitude',
  'warehouse_type',
  'status',
  'remarks',
  'verified_by',
] as const;

export const WAREHOUSE_STATUSES: { value: WarehouseStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'unknown', label: 'Unknown' },
];

/** Reserved masters (not implemented this sprint). */
export const RESERVED_MASTER_MODULES = [
  { id: 'marketplace', label: 'Marketplace Master', status: 'reserved' as const },
  { id: 'state', label: 'State Master', status: 'reserved' as const },
  { id: 'region', label: 'Region Master', status: 'reserved' as const },
  { id: 'category', label: 'Category Master', status: 'reserved' as const },
  { id: 'brand', label: 'Brand Master', status: 'reserved' as const },
  { id: 'supplier', label: 'Supplier Master', status: 'reserved' as const },
  { id: 'carrier', label: 'Carrier Master', status: 'reserved' as const },
] as const;
